import { useContext } from "react";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import styles from "../../../login/components/TeamSelection/TeamsModal/teams-modal.module.css";
import { SuperUsersTitle } from "./components/SuperUsersTitle/SuperUsersTitle";
import { UsersListView } from "./components/UsersListView/UsersListView";
import { useAtkomstSuperUsers } from "../../hooks/users/useAtkomstSuperUsers";
import {
  useStoreOwners,
  useCountrySuperUsers,
} from "../../hooks/users/useStoreSuperUsers";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

interface SuperUsersListModalProps {
  storeId?: string;
  /** Pass true in the new-user flow where the caller knows Atkomst should
   * be used but the user's permissions haven't loaded yet (not in DB). */
  forceAtkomst?: boolean;
}

export const SuperUsersListModal = ({
  storeId,
  forceAtkomst,
}: SuperUsersListModalProps = {}) => {
  const { modalState, toggleModal } = useContext(ModalContext);
  const { loggedInUser } = useLoggedInUser();
  const flagAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);
  const enableAtkomst = forceAtkomst ?? flagAtkomst;
  const { t } = useTranslation();

  const { data: atkomstSuperUsers } = useAtkomstSuperUsers(storeId, {
    enabled: enableAtkomst,
  });
  const dbStoreOwners = useStoreOwners(storeId, !enableAtkomst);
  const dbCountrySuperUsers = useCountrySuperUsers(storeId, !enableAtkomst);

  const storeSuperUsers = (
    enableAtkomst ? atkomstSuperUsers?.storeSuperUsers : dbStoreOwners
  )?.filter((user) => user.email !== loggedInUser.email);

  const countrySuperUsers = (
    enableAtkomst ? atkomstSuperUsers?.countrySuperUsers : dbCountrySuperUsers
  )?.filter((user) => user.email !== loggedInUser.email);
  return (
    <FixaModal
      visible={modalState.isSuperUsersListOpen}
      handleCloseBtn={() => {
        toggleModal({ type: ModalType.SuperUsersList });
      }}
    >
      <FixaSheets
        fullSize={false}
        labelledById="super-user-list-header"
        size="small"
        className={styles["teams-sheets"] as string}
        header={
          <FixaModalHeader
            id="super-user-list-header"
            className={styles["teams-header-title"] as string}
            title={`Fixa ${t("superUsersString")}`}
          ></FixaModalHeader>
        }
        footer={null}
      >
        <SuperUsersTitle
          title={t("storeSuperusersString")}
          subTitle={t("storeSuperUsersDescription")}
        />
        <UsersListView users={storeSuperUsers} id={"store_users"} />
        <SuperUsersTitle
          title={t("countrySuperusersString")}
          subTitle={t("countrySuperUsersDescription")}
        />
        <UsersListView users={countrySuperUsers} id={"country_users"} />
      </FixaSheets>
    </FixaModal>
  );
};
